import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { t as translate } from '../i18n/translations';
import { foodNameToEnglish } from '../i18n/foodTranslations';

const STORAGE_KEY = 'plansyncfit-locale';

/** @typedef {'en' | 'sr'} Locale */

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try {
      const s = localStorage.getItem(STORAGE_KEY);
      if (s === 'en' || s === 'sr') return s;
    } catch {
      /* ignore */
    }
    return 'sr';
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = locale === 'sr' ? 'sr-Latn' : 'en';
  }, [locale]);

  const setLocale = useCallback((next) => {
    setLocaleState(next === 'en' ? 'en' : 'sr');
  }, []);

  const t = useCallback((path) => translate(locale, path), [locale]);

  /**
   * @param {string} storedName Serbian name from DB / meal plan
   * @param {string|null|undefined} nameEn Optional English label from DB (foods.name_en)
   */
  const foodDisplayName = useCallback(
    (storedName, nameEn) => {
      if (!storedName) return '';
      if (locale !== 'en') return storedName;
      if (nameEn !== undefined && nameEn !== null && String(nameEn).trim() !== '') {
        return String(nameEn).trim();
      }
      return foodNameToEnglish(storedName);
    },
    [locale]
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
      foodDisplayName,
    }),
    [locale, setLocale, t, foodDisplayName]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}
